import * as _ from 'lodash';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import useToastedMutation from '../../ReactQuery/useToastedMutation';
import { get } from '../../redux/api';
import TeamsModule from '../../redux/teams';
import utils from '../../utils';
import DropdownButton from '../generic/DropdownButton';
import { icons, IconText } from '../generic/Icon';
import { useAddJobToNotificationQueue } from '../JobNotificationHub/Provider';
import { issueDashboardFeature } from '../settings/billing/features/consts';
import type { Task } from '../../types';
import type {
  QueuedReportTaskResponse,
  ReportDelivery,
} from '../../../shared/types/routes/queuedReports';

import DashboardReportCard from './DashboardReportCard';
import { addQueuedExportToNotificationQueue } from './GlobalIssueCSVExportCard';

const GenerateReportIconText = <IconText icon={icons.addToProject} text="Generate report" />;

/**
 * The pre-CORE-7309 "Global Issue CSV Export" card: a Download / Email dropdown over
 * `GET /api/v2/issues/csv/global` scoped to the org (or the globally selected teams). Rendered when the
 * `teamGroupIssueReport` flag is off, so the classic export is preserved during rollout. Delete this
 * component and its branch in DashboardActionCards when the flag is retired.
 */
export const LegacyGlobalIssueCSVExportCard: VoidFunctionComponent = () => {
  const selectedTeams = useSelector(TeamsModule.selectors.getTeamSelection);
  const addJob = useAddJobToNotificationQueue();

  // Both deliveries are background jobs, so a large org's export isn't cut off by the request timeout. The
  // mutation surfaces the outcome as a toast and hands the job to the notification queue on success.
  const queueExport = useToastedMutation({
    mutationFn: async (delivery: ReportDelivery) => {
      const response = await get<Task | QueuedReportTaskResponse>('/api/v2/issues/csv/global', false, {
        ...(delivery === 'email' ? { email: true } : { delivery: 'async-download' }),
        ...(!_.isEmpty(selectedTeams) ? { teamIds: selectedTeams } : {}),
      });
      if (!response.ok) {
        throw new Error('Failed to queue CSV export');
      }
      return response.data;
    },
    successToastMessage: (_data, delivery) => (delivery === 'email'
      ? 'Your report is being generated and will be emailed to you.'
      : "Your report is being generated. We'll let you know when it's ready."),
    errorToastMessage: 'Failed to generate the report.',
    onSuccess: (task) => addQueuedExportToNotificationQueue(addJob, task),
  });

  // DropdownButton's onSelect passes the selected item's eventKey.
  const generate = (eventKey?: unknown) => {
    // Premium gate — mirrors the old checkAccess paywall.
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal({ focusFeature: issueDashboardFeature });
      return;
    }

    queueExport.mutate(eventKey === 'email' ? 'email' : 'download');
  };

  return (
    <div>
      <DashboardReportCard
        title="Global Issue CSV Export"
        description="Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues."
      >
        <DropdownButton
          id="GlobalCSVExportV2Options"
          title={GenerateReportIconText}
          size="large"
          onSelect={generate}
          disabled={queueExport.isPending}
        >
          <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
          <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
        </DropdownButton>
      </DashboardReportCard>
    </div>
  );
};
